import {
  Actions,
  Async,
  FSpread,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { useSubscriptionBundlesModel } from '../model/subscriptionBundles.model';
import {
  BundlesCards,
  toHelpMixpanelEvent,
  HelpEventType,
  toBundleCards,
} from './subscriptionBundle.vm.mapper';
import { EditBundlePriceVM, useEditBundlePriceVM } from './editBundlePrice.vm';
import {
  EditBundleContentVM,
  useEditBundleContentVM,
} from './editBundleContent.vm';
import {
  EditPlatformBundleVM,
  useEditPlatformBundleVM,
} from './editPlatformBundle.vm';
import { MigrateToBundleVM, useMigrateToBundleVM } from './migrateToBundle.vm';
import { isEmpty } from 'lodash';
import { CreateBundleVM, useCreateBundleVM } from './createBundle.vm';
import { BundleStatus } from 'generated/graphql';
import {
  StartExclusiveBundleVM,
  useStartExclusiveBundleVM,
} from './startExclusiveBundle.vm';

export type BundlesVMData = {
  hideSubscriptionBundles: boolean;
  showEmptyState: boolean;
  showMigrateToBundle: boolean;
  exclusiveBundlePlaceholder: {
    show: boolean;
    create: () => void;
  };
  cards: BundlesCards;
  emptyState: {
    create: () => void;
  };
};

type PayloadMap = {
  helpMaterialsEvent: HelpEventType;
};

type SubscriptionBundleVMAction = Actions<PayloadMap>;
export type SubscriptionBundleVMDispatcher = FSpread<PayloadMap>;

export type SubscriptionBundlesVM = BundlesVMData &
  Pick<
    EditBundlePriceVM,
    | 'confirmPriceModal'
    | 'priceEditModal'
    | 'confirmPriceDisableModal'
    | 'confirmPublishModal'
    | 'linksModal'
  > &
  Pick<EditBundleContentVM, 'list' | 'confirmSaveModal'> &
  Pick<
    EditPlatformBundleVM,
    'platformBundleModal' | 'unsavedChangesModal' | 'pushNotificationModal'
  > &
  Pick<CreateBundleVM, 'createBundleModal'> &
  MigrateToBundleVM & {
    dispatch: SubscriptionBundleVMDispatcher;
  } & Pick<StartExclusiveBundleVM, 'startExlusiveBundleModal'>;

export const useSubscriptionBundlesVM = ({
  creatorId,
}: {
  creatorId: number;
}): Async<SubscriptionBundlesVM> => {
  const model = useSubscriptionBundlesModel({ creatorId });
  const {
    priceEditModal,
    confirmPriceModal,
    confirmPriceDisableModal,
    confirmPublishModal,
    editBundlePrice,
    submittingBundleIds,
    linksModal,
    openLinksModal,
  } = useEditBundlePriceVM({
    model,
    creatorId,
  });
  const { list, confirmSaveModal } = useEditBundleContentVM({ model });
  const {
    editPlatformBundle,
    platformBundleModal,
    unsavedChangesModal,
    pushNotificationModal,
  } = useEditPlatformBundleVM({ model, openLinksModal });
  const { migrateToBundle } = useMigrateToBundleVM({ creatorId, model });
  const refetch = model.type === 'fetched' ? model.value.refetch : undefined;
  const { createBundle, createBundleModal } = useCreateBundleVM({
    creatorId,
    refetch,
  });
  const { startExlusiveBundle, startExlusiveBundleModal } =
    useStartExclusiveBundleVM({
      creatorId,
      refetch,
    });

  if (model.type !== 'fetched') return model;

  const dispatch: SubscriptionBundleVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as SubscriptionBundleVMAction;
    switch (action.type) {
      case 'helpMaterialsEvent':
        toHelpMixpanelEvent(action.payload);
        return null;
    }
  };

  const publishedBundles = model.value.initialSubscriptionBundles.filter(
    (b) => b.status === BundleStatus.Published,
  );

  const hasDraftExclusiveBundle = Boolean(
    model.value.initialSubscriptionBundles.find(
      (b) => b.status === BundleStatus.Draft && !b.isPlatformBundle,
    ),
  );

  const isPlatformBundleActiveCreator =
    publishedBundles.length === 1 &&
    publishedBundles.every((b) => b.isPlatformBundle);
  const platformFreeTrial = model.value.enableCheckoutFreeTrial;

  return toAsyncFetched<SubscriptionBundlesVM>({
    dispatch,
    hideSubscriptionBundles: model.value.hideSubscriptionBundles,
    showEmptyState: isEmpty(model.value.initialSubscriptionBundles),
    showMigrateToBundle: !model.value.bundleBusinessModel,
    cards: toBundleCards(
      model.value.initialSubscriptionBundles,
      editBundlePrice,
      editPlatformBundle,
      submittingBundleIds,
      openLinksModal,
      isPlatformBundleActiveCreator,
      platformFreeTrial,
    ),
    list,
    confirmSaveModal,
    priceEditModal,
    confirmPriceModal,
    confirmPriceDisableModal,
    confirmPublishModal,
    exclusiveBundlePlaceholder: {
      show: isPlatformBundleActiveCreator && !hasDraftExclusiveBundle,
      create: startExlusiveBundle,
    },
    emptyState: {
      create: createBundle,
    },
    platformBundleModal,
    unsavedChangesModal,
    pushNotificationModal,
    migrateToBundle,
    startExlusiveBundleModal,
    createBundleModal,
    linksModal,
  });
};
